import { FloatingFormItem, InputSearch } from '@/components/_common';
import {
  useCreatePartReplacement,
  usePartReplacement,
} from '@/core/domain/operational/PartReplacement/PartReplacement.hooks';
import { PartReplacementsFormValues } from '@/core/domain/operational/PartReplacement/PartReplacement.types';
import { Part, PartsQuery } from '@/core/domain/operational/Parts/Parts.types';
import { useDisclosure } from '@/hooks';
import { Button, Col, Divider, Form, FormInstance, Row } from 'antd';
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import SelectPartReplacementList from './SelectPartReplacementList';
import SelectPartReplacementModal from './SelectPartReplacementModal';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface Props {
  form: FormInstance<PartReplacementsFormValues>;
}

const PartReplacementPartsForm = ({ form }: Props) => {
  const intl = useIntl().formatMessage;

  const { partId } = useRouteMatch<{ partId: string }>().params;

  const [isModalVisible, setIsModalVisible] = useDisclosure();
  const [selectPiece, setSelectPiece] = useState<PartsQuery>();

  const { data: partsReplacement } = usePartReplacement({
    partId: +partId,
  });

  const idsReplacements = partsReplacement
    ? partsReplacement?.data.map((part) => part.id)
    : [];

  const createPartReplacement = useCreatePartReplacement();

  useEffect(() => {
    form.setFieldsValue({
      name: selectPiece?.name,
    });
  }, [selectPiece]);

  const handlerOnOkay = () => {
    setIsModalVisible.toggle();
  };

  const handlerPartSelect = (part: Part) => {
    setSelectPiece(part);
    setIsModalVisible.toggle();
  };

  const addReplacementPart = () => {
    try {
      if (selectPiece) {
        createPartReplacement.mutateAsync({
          replacementId: selectPiece.id,
          partId: +partId,
        });
        form.resetFields();
        setSelectPiece(undefined);
      }
    } catch (error) {}
  };

  return (
    <Form form={form} preserve={false}>
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <FloatingFormItem
            label={intl({
              id: 'parts.partsReplacement.part',
            })}
            name="name"
            initialValue={selectPiece?.name}
          >
            <InputSearch
              buttonProps={{
                onClick: setIsModalVisible.toggle,
                disabled: !hasPermissions([
                  Authorities.PART_REPLACEMENT_CREATE,
                ]),
              }}
              inputProps={{
                disabled: true,
              }}
            />
          </FloatingFormItem>
        </Col>
      </Row>

      <SpaceForButton>
        <Button
          block
          disabled={
            !selectPiece ||
            idsReplacements.includes(Number(selectPiece?.id)) ||
            !hasPermissions([Authorities.PART_REPLACEMENT_CREATE])
          }
          onClick={() => addReplacementPart()}
        >
          {intl({ id: 'parts.partsReplacement.btn.addReplacementPart' })}
        </Button>
      </SpaceForButton>

      <Divider />

      <SelectPartReplacementList />

      <SelectPartReplacementModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible.toggle}
        handlerOnOkay={handlerOnOkay}
        handlerPartSelect={handlerPartSelect}
      />
    </Form>
  );
};

export default PartReplacementPartsForm;
